import React from 'react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Payments, AccountBalanceWallet, BuildCircle, TaskAlt } from '@mui/icons-material';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import TableCard from './TableCard';
import ChartTooltip from './ChartTooltip';
import { COLORS } from '../shared/tokens';
import { gbp, gbpCompact, shortWeek, SERIES, statusColor } from './format';

/* Repair brand is stored as a JSON string ({ value, label }) or a plain string. */
function deviceBrand(raw) {
    if (!raw) return 'N/A';
    try {
        const parsed = JSON.parse(raw);
        return parsed?.value || parsed?.label || String(raw);
    } catch (e) {
        return String(raw);
    }
}

const jobCols = [
    { key: 'brand', label: 'Device', render: (r) => deviceBrand(r.brand) },
    { key: 'cust', label: 'Customer', render: (r) => `${r.fname || ''} ${r.lname || ''}`.trim() || 'N/A' },
    { key: 'phone', label: 'Contact', render: (r) => r.phone || 'N/A' },
];

export default function ShopDashboard({ data }) {
    const { kpis, trend = [], jobStatus = [], inProcess = [], completed = [] } = data;
    const spark = trend.map((t) => ({ v: t.cash + t.card + t.other }));
    const pie = jobStatus.map((j) => ({ name: j.status, value: j.count }));

    return (
        <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <StatCard label="Latest Week Sales" value={gbpCompact(kpis.latestWeek)} sub={kpis.latestWeekCode} accent={COLORS.primary} icon={<Payments fontSize="small" />} spark={spark} />
                <StatCard label="8-Week Sales" value={gbpCompact(kpis.sales8)} sub={`through ${kpis.latestWeekCode}`} accent="#2563eb" icon={<Payments fontSize="small" />} />
                <StatCard label="Balance" value={gbp(kpis.balance)} sub="latest shop payment" accent="#15803d" icon={<AccountBalanceWallet fontSize="small" />} />
                <StatCard label="Jobs In Process" value={kpis.inProcess} accent="#a16207" icon={<BuildCircle fontSize="small" />} />
                <StatCard label="Jobs Completed" value={kpis.completed} accent="#15803d" icon={<TaskAlt fontSize="small" />} />
            </div>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <ChartCard
                    title="Weekly Sales" flex="2 1 460px"
                    legend={[{ label: 'Cash', color: SERIES.cash }, { label: 'Card', color: SERIES.card }, { label: 'Other', color: SERIES.other }]}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trend} margin={{ top: 6, right: 8, left: -12, bottom: 0 }} barSize={20}>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} vertical={false} />
                            <XAxis dataKey="week" tickFormatter={shortWeek} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={(v) => gbpCompact(v)} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} width={54} />
                            <Tooltip cursor={{ fill: COLORS.primaryTintSoft }} content={<ChartTooltip week />} />
                            <Bar dataKey="cash" stackId="s" fill={SERIES.cash} />
                            <Bar dataKey="card" stackId="s" fill={SERIES.card} />
                            <Bar dataKey="other" stackId="s" fill={SERIES.other} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Repair Status" subtitle="all jobs" flex="1 1 300px">
                    {pie.length === 0 ? (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.placeholder, fontSize: 13 }}>
                            No repairs recorded
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
                                    {pie.map((p, i) => <Cell key={i} fill={statusColor(p.name)} />)}
                                </Pie>
                                <Tooltip content={<ChartTooltip prefix="" />} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <TableCard title="In Process Jobs" accent="#a16207" count={inProcess.length} columns={jobCols} rows={inProcess} emptyLabel="No jobs in process" flex="1 1 380px" maxHeight={280} />
                <TableCard title="Completed Jobs" accent="#15803d" count={completed.length} columns={jobCols} rows={completed} emptyLabel="No completed jobs" flex="1 1 380px" maxHeight={280} />
            </div>
        </>
    );
}
